import { create } from 'zustand'
import { hsvaToRgba, hsvaToRgbString } from '@uiw/color-convert'

export type HSVA = {
  h: number
  s: number
  v: number
  a: number
}

export type HSL = { h: number; s: number; l: number }

interface ColorStore {
  primary: HSVA
  secondary: HSVA
  tertiary: HSVA
  background: HSVA
  setPrimary: (color: HSVA) => void
  setSecondary: (color: HSVA) => void
  setTertiary: (color: HSVA) => void
  setBackground: (color: HSVA) => void
  applyColors: () => void
}

const defaultPrimary: HSVA = { h: 0, s: 0, v: 100, a: 1 }
const defaultSecondary: HSVA = { h: 0, s: 0, v: 0, a: 1 }
const defaultTertiary: HSVA = { h: 24, s: 100, v: 100, a: 1 }
const defaultBackground: HSVA = { h: 24, s: 100, v: 40, a: 1 }

const generateRgbaString = (hsva: HSVA, alpha: number) => {
  const { r, g, b } = hsvaToRgba(hsva)
  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

const lastCssVars: Record<string, string> = {}
let rafId: number | null = null

const applyToCSSVariables = (
  primary: HSVA,
  secondary: HSVA,
  tertiary: HSVA,
  background: HSVA
) => {
  const root = document.documentElement

  const nextVars: Record<string, string> = {
    '--primary': hsvaToRgbString(primary),
    '--secondary': hsvaToRgbString(secondary),
    '--tertiary': hsvaToRgbString(tertiary),
    '--background': hsvaToRgbString(background)
  }

  for (let i = 1; i <= 6; i++) {
    nextVars[`--transparentPrimary${i}`] = generateRgbaString(primary, i * 0.1)
    nextVars[`--transparentSecondary${i}`] = generateRgbaString(
      secondary,
      i * 0.1
    )
  }

  if (rafId) cancelAnimationFrame(rafId)

  rafId = requestAnimationFrame(() => {
    Object.entries(nextVars).forEach(([key, value]) => {
      if (lastCssVars[key] !== value) {
        root.style.setProperty(key, value)
        lastCssVars[key] = value
      }
    })
  })
}

export const useColorStore = create<ColorStore>((set, get) => ({
  primary: defaultPrimary,
  secondary: defaultSecondary,
  tertiary: defaultTertiary,
  background: defaultBackground,

  setPrimary: color => set({ primary: color }),
  setSecondary: color => set({ secondary: color }),
  setTertiary: color => set({ tertiary: color }),
  setBackground: color => set({ background: color }),

  applyColors: () => {
    const { primary, secondary, tertiary, background } = get()
    applyToCSSVariables(primary, secondary, tertiary, background)
  }
}))
